import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SlidersHorizontal, Star, ArrowUpDown, X, Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { getCategories } from '@/lib/api';
import { logger } from '@/lib/logger';

export const FilterSortModal = ({ filters, onApplyFilters, onReset }) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [structuredCategories, setStructuredCategories] = useState({});
  const [specializationsByFag, setSpecializationsByFag] = useState({});
  const [localFilters, setLocalFilters] = useState(filters);
  const [specSearch, setSpecSearch] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getCategories();
        setCategories(response.data.categories);
        setStructuredCategories(response.data.structured || {});
        setSpecializationsByFag(response.data.specializations || {});
      } catch (error) {
        logger.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    onApplyFilters(localFilters);
    setOpen(false);
  };

  const handleReset = () => {
    const resetFilters = {
      category: '',
      specialization: '',
      minRating: 0,
      sortBy: 'rating',
      sortOrder: 'desc'
    };
    setLocalFilters(resetFilters);
    onReset();
    setOpen(false);
  };

  const activeFiltersCount = [
    localFilters.category,
    localFilters.specialization,
    localFilters.minRating > 0,
  ].filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          className="gap-2 border-primary/20 hover:border-primary hover:bg-primary/5"
          data-testid="filter-sort-button"
        >
          <SlidersHorizontal className="w-4 h-4" />
          Filtrer & Sortér
          {activeFiltersCount > 0 && (
            <span className="bg-primary text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center">
              {activeFiltersCount}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="filter-sort-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <SlidersHorizontal className="w-5 h-5" />
            Filtrer & Sortér
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Speciale-søgning på tværs af alle fag */}
          <div className="space-y-2" data-testid="specialization-search-block">
            <Label className="text-sm font-semibold flex items-center gap-2">
              <Search className="w-4 h-4" />
              Søg speciale (fx &quot;Badeværelse&quot;, &quot;Epoxy&quot;, &quot;Køkken&quot;)
            </Label>
            <Input
              type="text"
              value={specSearch}
              onChange={(e) => setSpecSearch(e.target.value)}
              placeholder="Skriv for at søge på tværs af alle fag..."
              className="h-10"
              data-testid="specialization-search-input"
            />
            {specSearch.trim().length >= 2 && (() => {
              const q = specSearch.trim().toLowerCase();
              const matches = [];
              Object.entries(specializationsByFag).forEach(([fag, specs]) => {
                specs.forEach((s) => {
                  if (s.toLowerCase().includes(q)) matches.push({ fag, spec: s });
                });
              });
              const shown = matches.slice(0, 12);
              if (shown.length === 0) {
                return <p className="text-xs text-muted-foreground italic">Ingen specialer matcher &quot;{specSearch}&quot;</p>;
              }
              return (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="specialization-search-results">
                  {shown.map(({ fag, spec }) => {
                    const active = localFilters.category === fag && localFilters.specialization === spec;
                    return (
                      <button
                        key={`${fag}-${spec}`}
                        type="button"
                        onClick={() => {
                          setLocalFilters({ ...localFilters, category: fag, specialization: spec });
                          setSpecSearch('');
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                          active
                            ? 'bronze-shine border-transparent text-white'
                            : 'bg-white border-stone-200 hover:border-[#c97f59] hover:text-[#c97f59]'
                        }`}
                        data-testid={`spec-search-result-${spec}`}
                      >
                        {spec} <span className="text-[10px] opacity-70">· {fag}</span>
                      </button>
                    );
                  })}
                  {matches.length > shown.length && (
                    <span className="text-xs text-muted-foreground self-center">
                      +{matches.length - shown.length} flere...
                    </span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Category Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Håndværkertype (fag)</Label>
            <Select
              value={localFilters.category || 'all'}
              onValueChange={(value) => setLocalFilters({ ...localFilters, category: value === 'all' ? '' : value, specialization: '' })}
            >
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder="Vælg kategori" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="all">Alle kategorier</SelectItem>
                {Object.entries(structuredCategories).sort(([a], [b]) => a.localeCompare(b, 'da')).map(([group, subcats]) => (
                  <div key={group}>
                    {Object.keys(structuredCategories).length > 1 && (
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">{group}</div>
                    )}
                    {subcats.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Specialization Filter (only if fag has specialer) */}
          {localFilters.category && (specializationsByFag[localFilters.category] || []).length > 0 && (
            <div className="space-y-3" data-testid="specialization-filter">
              <Label className="text-sm font-semibold">Speciale</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setLocalFilters({ ...localFilters, specialization: '' })}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                    !localFilters.specialization
                      ? 'bronze-shine border-transparent text-white'
                      : 'bg-white border-stone-200 hover:border-[#c97f59]'
                  }`}
                  data-testid="specialization-all"
                >
                  Alle specialer
                </button>
                {specializationsByFag[localFilters.category].map((spec) => {
                  const active = localFilters.specialization === spec;
                  return (
                    <button
                      key={spec}
                      type="button"
                      onClick={() => setLocalFilters({ ...localFilters, specialization: spec })}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                        active
                          ? 'bronze-shine border-transparent text-white'
                          : 'bg-white border-stone-200 hover:border-[#c97f59] hover:text-[#c97f59]'
                      }`}
                      data-testid={`filter-specialization-${spec}`}
                    >
                      {spec}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Rating Filter */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold flex items-center gap-2">
              <Star className="w-4 h-4 text-[#c97f59] fill-[#c97f59]" />
              Minimum bedømmelse
            </Label>
            <div className="flex items-center gap-4">
              <Slider
                value={[localFilters.minRating]}
                onValueChange={([value]) => setLocalFilters({ ...localFilters, minRating: value })}
                max={5}
                step={0.5}
                className="flex-1"
                data-testid="rating-slider"
              />
              <span className="text-sm font-medium w-12 text-right">
                {localFilters.minRating.toFixed(1)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0 stjerner</span>
              <span>5 stjerner</span>
            </div>
          </div>

          {/* Sort Options */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" />
              Rækkefølge
            </Label>
            <RadioGroup
              value={localFilters.sortOrder}
              onValueChange={(value) => setLocalFilters({ ...localFilters, sortOrder: value })}
              className="grid grid-cols-2 gap-2"
            >
              <div className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="desc" id="desc" data-testid="sort-desc" />
                <Label htmlFor="desc" className="cursor-pointer text-sm">Højest først</Label>
              </div>
              <div className="flex items-center space-x-2 border rounded-lg p-3 hover:bg-muted/50 cursor-pointer">
                <RadioGroupItem value="asc" id="asc" data-testid="sort-asc" />
                <Label htmlFor="asc" className="cursor-pointer text-sm">Lavest først</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={handleReset}
            className="flex-1 gap-2"
            data-testid="reset-filters-button"
          >
            <X className="w-4 h-4" />
            Nulstil
          </Button>
          <Button 
            onClick={handleApply}
            className="flex-1 bg-primary hover:bg-primary/90"
            data-testid="apply-filters-button"
          >
            Anvend filtre
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilterSortModal;
